// How It Works Section Component
// ===============================
// Feature #287: Updated with new frame styling and design-line accents

import React from 'react';
import { useTranslation } from 'react-i18next';
import DesignLine from '../ui/DesignLine';

interface Step {
  number: string;
  icon: string;
  titleKey: string;
  descriptionKey: string;
}

const steps: Step[] = [
  {
    number: '1',
    icon: '📚',
    titleKey: 'howItWorks.step1.title',
    descriptionKey: 'howItWorks.step1.description',
  },
  {
    number: '2',
    icon: '📝',
    titleKey: 'howItWorks.step2.title',
    descriptionKey: 'howItWorks.step2.description',
  },
  {
    number: '3',
    icon: '🎓',
    titleKey: 'howItWorks.step3.title',
    descriptionKey: 'howItWorks.step3.description',
  },
  {
    number: '4',
    icon: '💬',
    titleKey: 'howItWorks.step4.title',
    descriptionKey: 'howItWorks.step4.description',
  },
];

const HowItWorksSection: React.FC = () => {
  const { t } = useTranslation('landing');

  return (
    <section id="how-it-works" className="py-20 md:py-28 bg-background-light dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center mb-16">
          {/* Feature #280: Serif display font for headings */}
          <h2 className="text-3xl sm:text-4xl font-display font-bold text-neutral-dark dark:text-white mb-4">
            {t('howItWorks.title')}
          </h2>
          <p className="text-lg text-neutral-medium dark:text-gray-400 max-w-2xl mx-auto mb-6 font-sans">
            {t('howItWorks.subtitle')}
          </p>
          {/* Feature #287: Design-line accent */}
          <div className="flex justify-center">
            <DesignLine variant="horizontal" width="80px" height={2} />
          </div>
        </div>

        {/* Steps */}
        <div className="relative">
          {/* Connecting line (desktop) - Feature #287: Using design-line colors */}
          <div
            className="hidden lg:block absolute top-24 left-0 right-0 h-0.5"
            style={{ background: 'linear-gradient(to right, #4A6FA5, #D4845F)' }}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6">
            {steps.map((step, index) => (
              <div key={index} className="relative animate-fade-in-up" style={{ animationDelay: `${index * 0.15}s`, animationFillMode: 'both' }}>
                {/* Step card - Feature #287: New frame styling */}
                <div className="bg-canvas dark:bg-canvas-dark rounded-xl p-6 border border-frame-border dark:border-frame-border-dark hover:shadow-md transition-shadow">
                  {/* Number badge - Feature #287: Using design-line-blue */}
                  <div className="absolute -top-4 left-6 w-8 h-8 bg-design-line-blue text-white rounded-full flex items-center justify-center font-bold text-sm shadow-md">
                    {step.number}
                  </div>

                  {/* Icon */}
                  <div className="w-14 h-14 bg-primary-50 dark:bg-primary-900/30 rounded-lg flex items-center justify-center mb-4 mt-2">
                    <span className="text-2xl">{step.icon}</span>
                  </div>

                  {/* Title */}
                  <h3 className="text-lg font-semibold text-neutral-dark dark:text-white mb-2">
                    {t(step.titleKey)}
                  </h3>

                  {/* Description */}
                  <p className="text-neutral-medium dark:text-gray-400 text-sm leading-relaxed font-sans">
                    {t(step.descriptionKey)}
                  </p>
                </div>

                {/* Arrow (mobile/tablet) - Feature #287: Using design-line-orange */}
                {index < steps.length - 1 && (
                  <div className="flex justify-center my-4 lg:hidden">
                    <svg
                      className="w-6 h-6 text-design-line-orange"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 14l-7 7m0 0l-7-7m7 7V3"
                      />
                    </svg>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
